import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { API_BASE } from '../../config/api';

export const HeaderSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [formData, setFormData] = useState({
    menu_items: [],
    cta: { text: 'Consult Us', url: '#', enabled: true }
  });

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${backendUrl}/api/admin/header-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch settings');

      const data = await response.json();
      // Ensure mega_columns exists for each menu item
      const menuItems = (data.settings?.menu_items || []).map(item => ({
        ...item,
        mega_menu_enabled: item.mega_menu_enabled || false,
        mega_columns: item.mega_columns || [
          { title: '', links: [] },
          { title: '', links: [] },
          { title: '', links: [] }
        ]
      }));
      
      setFormData({
        menu_items: menuItems,
        cta: data.settings?.cta || { text: 'Consult Us', url: '#', enabled: true }
      });
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/header-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save');
      alert('Header settings saved successfully!');
    } catch (err) {
      alert('Failed to save header settings');
    } finally {
      setSaving(false);
    }
  };

  const addMenuItem = () => {
    setFormData(prev => ({
      ...prev,
      menu_items: [...prev.menu_items, { 
        label: '', 
        url: '', 
        enabled: true,
        mega_menu_enabled: false,
        mega_columns: [
          { title: '', links: [] },
          { title: '', links: [] },
          { title: '', links: [] }
        ]
      }]
    }));
  };

  const removeMenuItem = (index) => {
    setFormData(prev => ({
      ...prev,
      menu_items: prev.menu_items.filter((_, i) => i !== index)
    }));
    if (expandedMenu === index) setExpandedMenu(null);
  };

  const updateMenuItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      menu_items: prev.menu_items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const moveMenuItem = (index, direction) => {
    const newItems = [...formData.menu_items];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newItems.length) return;
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    setFormData(prev => ({ ...prev, menu_items: newItems }));
  };

  // Mega Menu Column Management
  const updateColumnTitle = (menuIndex, colIndex, title) => {
    setFormData(prev => ({
      ...prev,
      menu_items: prev.menu_items.map((item, i) => {
        if (i !== menuIndex) return item;
        const newColumns = [...item.mega_columns];
        newColumns[colIndex] = { ...newColumns[colIndex], title };
        return { ...item, mega_columns: newColumns };
      })
    }));
  };

  const addColumnLink = (menuIndex, colIndex) => {
    setFormData(prev => ({
      ...prev,
      menu_items: prev.menu_items.map((item, i) => {
        if (i !== menuIndex) return item;
        const newColumns = [...item.mega_columns];
        newColumns[colIndex] = {
          ...newColumns[colIndex],
          links: [...newColumns[colIndex].links, { label: '', url: '' }]
        };
        return { ...item, mega_columns: newColumns };
      })
    }));
  };

  const removeColumnLink = (menuIndex, colIndex, linkIndex) => {
    setFormData(prev => ({
      ...prev,
      menu_items: prev.menu_items.map((item, i) => {
        if (i !== menuIndex) return item;
        const newColumns = [...item.mega_columns];
        newColumns[colIndex] = {
          ...newColumns[colIndex],
          links: newColumns[colIndex].links.filter((_, li) => li !== linkIndex)
        };
        return { ...item, mega_columns: newColumns };
      })
    }));
  };

  const updateColumnLink = (menuIndex, colIndex, linkIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      menu_items: prev.menu_items.map((item, i) => {
        if (i !== menuIndex) return item;
        const newColumns = [...item.mega_columns];
        newColumns[colIndex] = {
          ...newColumns[colIndex],
          links: newColumns[colIndex].links.map((link, li) =>
            li === linkIndex ? { ...link, [field]: value } : link
          )
        };
        return { ...item, mega_columns: newColumns };
      })
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading settings...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#111827] mb-2" data-testid="header-settings-title">Header Settings</h1>
            <p className="text-[#6B7280]">Manage navigation menu, mega menus, and CTA button</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-gradient-to-r from-[#1E40AF] to-[#1e3a8a] hover:shadow-xl text-white font-semibold rounded-lg transition-all disabled:opacity-50"
            data-testid="save-header-button"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Menu Items */}
        <div className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-[#111827]">Menu Items</h2>
            <button
              onClick={addMenuItem}
              className="text-[#1E40AF] hover:text-[#1e3a8a] font-semibold text-sm flex items-center gap-2"
              data-testid="add-menu-item"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Item
            </button>
          </div>

          <div className="space-y-4">
            {formData.menu_items.map((item, index) => (
              <div key={index} className="border-2 border-[#E5E7EB] rounded-lg overflow-hidden" data-testid={`menu-item-${index}`}>
                {/* Main Menu Item Row */}
                <div className="p-4 bg-[#F8FAFC]">
                  <div className="flex items-center gap-4">
                    {/* Reorder buttons */}
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => moveMenuItem(index, -1)}
                        disabled={index === 0}
                        className="p-1 text-[#6B7280] hover:text-[#111827] disabled:opacity-30"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => moveMenuItem(index, 1)}
                        disabled={index === formData.menu_items.length - 1}
                        className="p-1 text-[#6B7280] hover:text-[#111827] disabled:opacity-30"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>

                    {/* Label */}
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-[#6B7280] mb-1">Label</label>
                      <input
                        type="text"
                        value={item.label}
                        onChange={(e) => updateMenuItem(index, 'label', e.target.value)}
                        className="w-full px-3 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] text-sm"
                        placeholder="e.g., Universities"
                      />
                    </div>

                    {/* URL */}
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-[#6B7280] mb-1">URL</label>
                      <input
                        type="text"
                        value={item.url}
                        onChange={(e) => updateMenuItem(index, 'url', e.target.value)}
                        className="w-full px-3 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] text-sm"
                        placeholder="e.g., /universities"
                      />
                    </div>

                    {/* Toggles */}
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={item.enabled}
                          onChange={(e) => updateMenuItem(index, 'enabled', e.target.checked)}
                          className="w-4 h-4"
                        />
                        <span className="text-xs text-[#6B7280]">Enabled</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={item.mega_menu_enabled}
                          onChange={(e) => updateMenuItem(index, 'mega_menu_enabled', e.target.checked)}
                          className="w-4 h-4"
                        />
                        <span className="text-xs text-[#6B7280]">Mega Menu</span>
                      </label>
                    </div>

                    {/* Expand/Collapse Mega Menu */}
                    {item.mega_menu_enabled && (
                      <button
                        onClick={() => setExpandedMenu(expandedMenu === index ? null : index)}
                        className="p-2 text-[#1E40AF] hover:bg-[#EFF6FF] rounded-lg"
                      >
                        <svg className={`w-5 h-5 transition-transform ${expandedMenu === index ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    )}

                    {/* Delete */}
                    <button
                      onClick={() => removeMenuItem(index)}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Mega Menu Columns (Expandable) */}
                {item.mega_menu_enabled && expandedMenu === index && (
                  <div className="p-4 border-t-2 border-[#E5E7EB]">
                    <p className="text-sm font-semibold text-[#111827] mb-4">Mega Menu Columns (3 columns)</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {[0, 1, 2].map(colIndex => (
                        <div key={colIndex} className="border-2 border-[#E5E7EB] rounded-lg p-3">
                          <div className="mb-3">
                            <label className="block text-xs font-medium text-[#6B7280] mb-1">Column {colIndex + 1} Title</label>
                            <input
                              type="text"
                              value={item.mega_columns[colIndex]?.title || ''}
                              onChange={(e) => updateColumnTitle(index, colIndex, e.target.value)}
                              className="w-full px-3 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] text-sm"
                              placeholder="e.g., By Country"
                            />
                          </div>

                          <div className="space-y-2 mb-3">
                            {(item.mega_columns[colIndex]?.links || []).map((link, linkIndex) => (
                              <div key={linkIndex} className="flex gap-2">
                                <input
                                  type="text"
                                  value={link.label}
                                  onChange={(e) => updateColumnLink(index, colIndex, linkIndex, 'label', e.target.value)}
                                  className="flex-1 px-2 py-1.5 border border-[#E5E7EB] rounded text-xs"
                                  placeholder="Label"
                                />
                                <input
                                  type="text"
                                  value={link.url}
                                  onChange={(e) => updateColumnLink(index, colIndex, linkIndex, 'url', e.target.value)}
                                  className="flex-1 px-2 py-1.5 border border-[#E5E7EB] rounded text-xs"
                                  placeholder="URL"
                                />
                                <button
                                  onClick={() => removeColumnLink(index, colIndex, linkIndex)}
                                  className="p-1 text-red-500 hover:text-red-700"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            ))}
                          </div>

                          <button
                            onClick={() => addColumnLink(index, colIndex)}
                            className="text-[#1E40AF] hover:text-[#1e3a8a] text-xs font-semibold flex items-center gap-1"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add Link
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {formData.menu_items.length === 0 && (
              <p className="text-[#6B7280] text-sm text-center py-4">No menu items. Click "Add Item" to create one.</p>
            )}
          </div>
        </div>

        {/* CTA Button */}
        <div className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6">
          <h2 className="text-lg font-bold text-[#111827] mb-4">CTA Button</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Button Text</label>
              <input
                type="text"
                value={formData.cta.text}
                onChange={(e) => setFormData(prev => ({ ...prev, cta: { ...prev.cta, text: e.target.value } }))}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                placeholder="e.g., Consult Us"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Button URL</label>
              <input
                type="text"
                value={formData.cta.url}
                onChange={(e) => setFormData(prev => ({ ...prev, cta: { ...prev.cta, url: e.target.value } }))}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                placeholder="e.g., /contact or #"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer pb-2">
                <input
                  type="checkbox"
                  checked={formData.cta.enabled}
                  onChange={(e) => setFormData(prev => ({ ...prev, cta: { ...prev.cta, enabled: e.target.checked } }))}
                  className="w-5 h-5"
                />
                <span className="text-sm font-semibold text-[#111827]">Show CTA Button</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default HeaderSettings;
